import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";

import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import Checkbox from "../../../form/Checkbox";
import Textarea from "../../../form/Textarea";
import Radio from "../../../form/Radio";
import DatePicker from "../../../form/DatePicker";
import { dynamicFormList } from "../../../../services/dynamicFormService";
import DynamicFormLoader from "../../../loader/DynamicFormLoader";
import { classNames } from "../../../../helpers/classNames";
import Errors from "../../../elements/Errors";
import { lngDetect } from "../../../../helpers/lngDetect";

const DynamicForm = ({
  values,
  handleChange = () => {},
  setFieldsValue = () => {},
  errors = {},
  formValidate = () => {},
  setValidationRules = () => {},
  sectionFor,
  category,
  validationStartFrom,
}) => {
  const { event: eventDetailsData } = useOutletContext();
  const queryClient = useQueryClient();

  const getQuestionType = useCallback((field) => {
    const fieldLabel =
      lngDetect(field.label?.text)?.toLowerCase() ||
      field.label?.toLowerCase() ||
      "";
    if (fieldLabel.includes("name")) return "name";
    if (fieldLabel.includes("email")) return "email";
    return "custom";
  }, []);

  // Process form fields into questions
  const processFormFields = ({ fields, sectionData, form }) => {
    if (!fields?.length) return { questions: [], rules: {} };

    const sortedFields = [...fields].sort(
      (a, b) => (a.ordering || 0) - (b.ordering || 0)
    );

    const questions = sortedFields.flatMap((field) => {
      const label = lngDetect(field.label?.text) || "Untitled";
      const questionType = getQuestionType(field);

      if (label === "First Name | Last Name") {
        return label.split("|").map((f) => {
          return {
            formId: form?._id,
            sectionId: sectionData?._id,
            id: field._id,
            type: field.type,
            label: f.trim(),
            questionType,
            validation: field?.validation || false,
            placeholder: `Enter ${f.trim()}`,
            defaultValue: field.defaultValue || false,
            ...(field?.fieldFor && field?.fieldFor !== "all"
              ? { showField: field?.fieldFor === category ? "yes" : "no" }
              : { showField: "yes" }),
            options:
              field.options?.map((opt) => ({
                label: lngDetect(opt.label?.text) || "",
                value: lngDetect(opt.key?.text),
                default: opt.default || false,
              })) || [],
            required: true,
            ordering: field.ordering || 0,
          };
        });
      }

      return {
        formId: form?._id,
        sectionId: sectionData?._id,
        id: field._id,
        type: field.type,
        label,
        questionType,
        placeholder: lngDetect(field.placeholder) || "",
        defaultValue: field.defaultValue || false,
        validation: field?.validation || false,
        ...(field?.fieldFor && field?.fieldFor !== "all"
          ? { showField: field?.fieldFor === category ? "yes" : "no" }
          : { showField: "yes" }),
        options:
          field.options?.map((opt) => {
            return {
              name: lngDetect(opt.label?.text) || "",
              label: lngDetect(opt.label?.text) || "",
              value: lngDetect(opt.label?.text) || "",
              default: opt.default || false,
            };
          }) || [],
        required: field.required || false,
        ordering: field.ordering || 0,
      };
    });

    return questions?.filter((qs) => qs?.showField === "yes");
  };

  const {
    data: globalData,
    isLoading: globalDataLoading,
    isFetching: globalDataFetching,
  } = useQuery({
    queryKey: ["global-checkoutForm", sectionFor],
    enabled: !!sectionFor,
    queryFn: async () => {
      const res = await dynamicFormList({
        sortQuery: "ordering",
        type: "global-checkout",
      });
      if (res?.docs?.status === 200) {
        const data = res?.docs?.data?.map((item) => ({
          ...item,
          sectionDetail:
            item?.sectionDetail?.filter(
              (sec) => sec?.sectionFor === sectionFor
            ) || {},
        }));
        return { ...res?.docs, data };
      } else {
        toast.warning(res?.message);
        return {};
      }
    },
  });

  const {
    data: sectionData,
    isLoading: sectionDataLoading,
    isFetching: sectionDataFetching,
  } = useQuery({
    queryKey: [
      "checkoutForm",
      ...(eventDetailsData?.data?.checkoutForm === "event"
        ? [eventDetailsData?.data?._id, "event-checkout"]
        : [
            eventDetailsData?.data?.organizationData?._id,
            "organization-checkout",
          ]),
      sectionFor,
    ],
    enabled: !!eventDetailsData?.data?._id && !!sectionFor,
    queryFn: async () => {
      queryClient.setQueriesData(
        { queryKey: ["checkoutForm"], exact: false },
        (state) => {
          return {};
        }
      );
      setValidationRules("reset");
      const res = await dynamicFormList({
        sortQuery: "ordering",
        ...(eventDetailsData?.data?.checkoutForm === "event"
          ? { event: eventDetailsData?.data?._id, type: "event-checkout" }
          : {
              organization: eventDetailsData?.data?.organizationData?._id,
              type: "organization-checkout",
            }),
      });

      if (res?.docs?.status === 200) {
        const data = res?.docs?.data?.map((item) => ({
          ...item,
          sectionDetail:
            item?.sectionDetail?.filter(
              (sec) => sec?.sectionFor === sectionFor
            ) || {},
        }));
        return { ...res?.docs, data };
      } else {
        toast.warning(res?.message);
        return {};
      }
    },
  });

  const fields = [
    globalData?.data?.map((item) =>
      item?.sectionDetail?.map((sec) =>
        processFormFields({
          fields: sec?.relatedField,
          sectionData: sec,
          form: item,
        })
      )
    ),
    sectionData?.data?.map((item) =>
      item?.sectionDetail?.map((sec) =>
        processFormFields({
          fields: sec?.relatedField,
          sectionData: sec,
          form: item,
        })
      )
    ),
  ]
    ?.filter(Boolean)
    ?.flat(Infinity);

  useEffect(() => {
    if (fields?.length > 0) {
      const rules = {};
      fields?.forEach((f) => {
        if (f?.required) {
          const field_name = validationStartFrom
            ? `${validationStartFrom || ""}[${f?.label}]`
            : f?.label;

          rules[field_name] = [
            {
              required: true,
              message: `${f?.label} is required`,
            },
            ...(f?.validation && f?.validation?.minLength
              ? [
                  {
                    type: "range",
                    min: f?.validation?.minLength,
                    message: `minimum ${f?.validation?.minLength} length is required`,
                  },
                ]
              : []),
            ...(f?.validation && f?.validation?.maxLength
              ? [
                  {
                    type: "range",
                    max: f?.validation?.maxLength,
                    message: `You can input maximum ${f?.validation?.maxLength}`,
                  },
                ]
              : []),
          ];
        }
      });

      setValidationRules(rules);
    }
  }, [fields?.length, eventDetailsData?.data?.checkoutForm]);

  return (
    <div>
      {globalDataLoading ||
      globalDataFetching ||
      sectionDataLoading ||
      sectionDataFetching ? (
        <DynamicFormLoader />
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {fields &&
            Array.isArray(fields) &&
            fields.map((field, i) => {
              const field_name = validationStartFrom
                ? `${validationStartFrom || ""}[${field?.label}]`
                : field?.label;
              return (
                <div
                  key={i?.toString()}
                  className={classNames(
                    ["textarea", "radio", "checkbox"]?.includes(field?.type)
                      ? "col-span-12"
                      : "col-span-12 lg:col-span-6"
                  )}
                >
                  {field?.type === "email" && (
                    <Input
                      value={values[field_name]}
                      inputPlaceholder={field?.placeholder || field?.label}
                      inputName={field_name}
                      label={field?.label}
                      inputType="email"
                      onChange={handleChange}
                      {...formValidate(errors, field_name)}
                    />
                  )}
                  {field?.type === "input" && (
                    <Input
                      value={values[field_name]}
                      inputName={field_name}
                      label={field?.label}
                      inputPlaceholder={field?.placeholder || field?.label}
                      onChange={handleChange}
                      {...formValidate(errors, field_name)}
                    />
                  )}
                  {field?.type === "textarea" && (
                    <Textarea
                      inputValue={values[field_name]}
                      inputName={field_name}
                      label={field?.label}
                      inputPlaceholder={field?.placeholder || field?.label}
                      onChange={handleChange}
                      {...formValidate(errors, field_name)}
                    />
                  )}
                  {field?.type === "date" && (
                    <DatePicker
                      label={field?.label}
                      placeholder={field?.placeholder || "Select date"}
                      value={{
                        startDate: values[field_name]?.startDate,
                        endDate: values[field_name]?.startDate,
                      }}
                      onChange={(val) => {
                        handleChange({
                          target: {
                            name: field_name,
                            value: val?.startDate || val?.endDate,
                          },
                        });
                      }}
                      {...formValidate(errors, field_name)}
                    />
                  )}
                  {field?.type === "select" && (
                    <Select
                      label={field?.label}
                      placeholder={field?.placeholder || "Select option"}
                      dropdownButtonClass={"rounded-md h-10"}
                      dropdownData={field?.options}
                      selectedValue={values[field_name]}
                      onChange={(val) => {
                        handleChange({
                          target: {
                            name: field_name,
                            value: val,
                          },
                        });
                      }}
                      {...formValidate(errors, field_name)}
                    />
                  )}
                  {field?.type === "radio" && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-text-disabled mb-1 font-CanelaDeck">
                        {field?.label}{" "}
                        <span className="text-red-500">
                          {field?.required && "*"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-y-1">
                        {field?.options?.map((opt) => (
                          <Radio
                            radioLabel={opt?.label}
                            radioName={field_name}
                            value={opt?.value}
                            checked={values[field_name] === opt?.value}
                            onChange={(e) => {
                              const name = e.target.name;
                              const value = e.target.value;
                              setFieldsValue({
                                [name]: value,
                              });
                            }}
                          />
                        ))}
                      </div>
                      <div>
                        <Errors {...formValidate(errors, field_name)} />
                      </div>
                    </div>
                  )}
                  {field?.type === "checkbox" && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-text-disabled mb-1 font-CanelaDeck">
                        {field?.label}{" "}
                        <span className="text-red-500">
                          {field?.required && "*"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-y-1">
                        {field?.options?.map((opt, idx) => {
                          const checkedValues = values?.[field_name] || [];
                          return (
                            <Checkbox
                              key={idx}
                              checkboxLabel={opt?.label}
                              checkboxName={field_name}
                              value={opt?.value}
                              checked={checkedValues.includes(opt?.value)}
                              onChange={(e) => {
                                const name = e.target.name;
                                const value = e.target.value;

                                const currentValues = values?.[name] || [];
                                const updatedValues = e.target.checked
                                  ? [...currentValues, value] // add value
                                  : currentValues.filter((v) => v !== value); // remove value

                                setFieldsValue({
                                  [name]: updatedValues,
                                });
                              }}
                            />
                          );
                        })}
                      </div>
                      <div>
                        <Errors {...formValidate(errors, field_name)} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default DynamicForm;
